<template>
    <v-card>
        <v-card-title>
            <div class="mr-3">{{ $t('event.promo-code') }}</div>
            <v-spacer></v-spacer>
            <v-btn v-if="isEditOrderMode" color="red" dark class="ml-3" @click="cancelEditOrder">
                {{ $t('Cancel') }}
                <v-icon right>mdi-close-circle-outline</v-icon>
            </v-btn>
            <v-btn v-if="isEditOrderMode" color="green" dark class="ml-3" @click="saveEditOrder">
                {{ $t('Save') }}
                <v-icon right>mdi-check</v-icon>
            </v-btn>
            <v-btn v-if="!isEditOrderMode" color="primary" @click="initEditOrder">
                {{ $t('Reorder') }}
                <v-icon right>mdi-swap-vertical</v-icon>
            </v-btn>
            <div class="button-spacer"></div>
            <v-btn color="primary" @click="initAddPromoCode">
                {{ $t('add') }}
                <v-icon right>mdi-plus</v-icon>
            </v-btn>
        </v-card-title>
        <v-data-table :headers="headers" :items="tablePromoCode" :loading="isPromoCodePending" @pagination="currentPagination">
            <template v-slot:item.scroller="{ value }">
                <div v-show="isEditOrderMode" class="handle"><v-btn icon><v-icon>drag_handle{{ value }}</v-icon></v-btn>
                </div>
                <div v-show="!isEditOrderMode"></div>
            </template>
            <template v-slot:item.order="{ value }">
                {{ value }}
            </template>
            <template v-slot:item.actions="{ item }">
                <v-btn color="primary" class="ma-2" :disabled="isEditOrderMode" @click="initEditPromoCode(item)">
                    {{ $t('edit') }}
                    <v-icon right>mdi-pencil</v-icon>
                </v-btn>
                <v-btn color="red" :dark="!isEditOrderMode" class="ma-2" :disabled="isEditOrderMode"
                    @click="deletePromoCode(item)">
                    {{ $t('delete') }}
                    <v-icon right>mdi-delete</v-icon>
                </v-btn>
            </template>
        </v-data-table>

        <v-dialog v-model="editPromoCodeDialogShown" max-width="600px" persistent scrollable :retain-focus="false">
            <v-card>
                <v-card-title>
                    {{ $t('event.promo-code') }}
                </v-card-title>
                <v-card-text>
                    <v-container>
                        <v-form v-model="isEditPromoCodeFormValid">
                            <v-row>
                                <v-col class="mb-0 pb-0">
                                    <v-select v-if="types.length > 0" outlined :label="$t('event_promocode.type')"
                                        :placeholder="$t('event_promocode.type')"
                                        :items="types"
                                        v-model="editingPromoCode.type"></v-select>
                                    <v-text-field v-else outlined :label="$t('event_promocode.type')"
                                        :placeholder="$t('event_promocode.type')"
                                        v-model="editingPromoCode.type"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="mb-0 pb-0">
                                    <v-text-field outlined :label="$t('event_promocode.code')"
                                        :placeholder="$t('event_promocode.code')"
                                        v-model="editingPromoCode.code"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="mb-0 pb-0">
                                    <v-text-field outlined :label="$t('event_promocode.quota')"
                                        :placeholder="$t('event_promocode.quota')"
                                        v-model="editingPromoCode.quota"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="mb-0 pb-0">
                                    <v-text-field outlined :label="$t('event_promocode.used')"
                                        :placeholder="$t('event_promocode.used')"
                                        hint="This field is read-only"
                                        persistent-hint
                                        readonly
                                        v-model="editingPromoCode.used"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="mb-0 pb-0">
                                    <v-text-field outlined :label="$t('event_promocode.remarks')"
                                        :placeholder="$t('event_promocode.remarks')"
                                        v-model="editingPromoCode.remarks"></v-text-field>
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-btn color="secondary" @click="confirmCancelItemDialogShown = true">{{ $t('cancel') }}</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="confirmSaveItemDialogShown = true"
                        :disabled="!isEditPromoCodeFormValid">{{ $t('save') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-overlay :z-index="999" v-model="overlay">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
        <PopupAlert v-model="confirmCancelItemDialogShown" title="Warning"
            message="Do you wish to cancel your amendments?" :onYes="cancelEditPromoCodeItem"
            :onNo="function () { confirmCancelItemDialogShown = false; }"></PopupAlert>
        <PopupAlert v-model="confirmSaveItemDialogShown" title="Confirm"
            message="Do you wish to confirm to save your promocode?" :onYes="savePromoCode"
            :onNo="function () { confirmSaveItemDialogShown = false; }"></PopupAlert>
    </v-card>
</template>

<style scoped>
    .button-spacer {
        width: 10px;
    }
</style>

<script>
import { computed, ref, onMounted } from "@vue/composition-api";
import { useFind } from "feathers-vuex";
import { DateTime } from "luxon";
import { cloneDeep } from "lodash";
import PopupAlert from "../../components/PopupAlert";
import Sortable from "sortablejs";

export default {
    name: 'EventPromoCode',
    components: { PopupAlert },
    setup(props, { root }) {
        const { EventPromoCode } = root.$FeathersVuex.api;

        const eventId = computed(() => root.$route.params.eventId);

        const promoCodeParams = computed(() => {
            return {
                query: {
                    eventId: eventId.value
                }
            }
        });
        const { items: promoCodeItems, isPending: isPromoCodePending } = useFind({
            model: EventPromoCode,
            params: promoCodeParams,
        });
        // Data Table
        const headers = ref([
            { text: '', value: 'scroller', width: 80 },
            { text: root.$t('event_promocode.order'), value: 'order' },
            { text: root.$t('event_promocode.type'), value: 'type' },
            { text: root.$t('event_promocode.code'), value: 'code' },
            { text: root.$t('event_promocode.quota'), value: 'quota' },
            { text: root.$t('event_promocode.used'), value: 'used' },
            { text: root.$t('event_promocode.remarks'), value: 'remarks' },
            { text: '', value: 'actions' },
        ]);

        const types = computed(() => {
            switch (eventId.value) {
                // Pasla Conference 2026
                case 'a0711058-fc9a-4bd2-9a22-4886810ab3bb': // Production
                case 'e96ab950-e3d6-4b52-917d-3fcc00614735': // UAT
                    return ['Speaker', 'Sponsor', 'Media', 'PASLA Board Member', 'PASLA Staff', '2026 New Member', 'Other'];
                default:
                    return [];
            }
        });

        // Order
        const isEditOrderMode = ref(false);
        const didOrderChange = ref(false);
        const editOrderPromoCode = ref([]);
        const overlay = ref(false);

        // Add / Edit
        const editPromoCodeDialogShown = ref(false);
        const confirmCancelItemDialogShown = ref(false);
        const confirmSaveItemDialogShown = ref(false);
        const isEditPromoCodeFormValid = ref(false);
        const editingPromoCode = ref(new EventPromoCode({ eventId: eventId.value }));
        const initAddPromoCode = function () {
            editingPromoCode.value = new EventPromoCode({ eventId: eventId.value });
            editingPromoCode.value.order = promoCodeItems.value.length + 1;
            editPromoCodeDialogShown.value = true;
        };
        const initEditPromoCode = function (promocode) {
            editingPromoCode.value = cloneDeep(promocode);
            editPromoCodeDialogShown.value = true;
        };
        const deletePromoCode = function (promocode) {
            if (confirm(root.$t('event_promocode.confirm_delete'))) {
                promocode.remove();
            }
        };
        const savePromoCode = async function () {
            // Prevent the used value being modified
            delete editingPromoCode.value.used;
            editingPromoCode.value.save().then(() => {
                EventPromoCode.get(editingPromoCode.value.id);
                editPromoCodeDialogShown.value = false;
                confirmSaveItemDialogShown.value = false;
                root.$store.dispatch('showSnackBar', root.$t('event_promocode.save_successful'));
            }, root.$handleFeatherError.bind(root));
        };

        const previewHtmlContentOption = {
            modules: {
                toolbar: false,
            },
            placeholder: '',
            theme: 'bubble',
        };

        const cancelEditPromoCodeItem = function () {
            editPromoCodeDialogShown.value = false;
            confirmCancelItemDialogShown.value = false;
        }

        const initEditOrder = function () {
            didOrderChange.value = false
            isEditOrderMode.value = true
            editOrderPromoCode.value = cloneDeep(sortedPromoCode.value)
        }

        const saveEditOrder = async function () {
            if (!didOrderChange.value) {
                isEditOrderMode.value = false
                return;
            }

            overlay.value = true
            for (let i = 0; i < editOrderPromoCode.value.length; i++) {
                await editOrderPromoCode.value[i].save().then(() => {
                    EventPromoCode.get(editOrderPromoCode.value[i].id)
                })
            }
            isEditOrderMode.value = false
            overlay.value = false
        }

        const cancelEditOrder = function () {
            isEditOrderMode.value = false
        }
        const quickSortPromoCode = function (sortPromoCode) {
            if (sortPromoCode.length < 2) {
                return sortPromoCode
            }

            const chosenIndex = sortPromoCode.length - 1
            const chosen = sortPromoCode[chosenIndex]
            const a = []
            const b = []
            for (let i = 0; i < chosenIndex; i++) {
                const temp = sortPromoCode[i].order
                temp < chosen.order ? a.push(sortPromoCode[i]) : b.push(sortPromoCode[i])
            }
            const output = [...quickSortPromoCode(a), chosen, ...quickSortPromoCode(b)]
            return output
        }

        const sortedPromoCode = computed(() => {
            return quickSortPromoCode(promoCodeItems.value)
        })
        const currentStart = ref(0);// Holds the index of starting item in speakerItemList in the current page of v-table
        const currentPagination = function (e) { // Update current page
            currentStart.value = e.pageStart
        }
        onMounted(() => {
            let table = document.querySelector('.v-data-table tbody');
            Sortable.create(table,
                {
                    handle: ".handle", // Look for class with name "handle"
                    onEnd({ newIndex, oldIndex }) {
                        didOrderChange.value = true;
                        newIndex += currentStart.value;
                        oldIndex += currentStart.value;
                        const rowSelected = editOrderPromoCode.value.splice(oldIndex, 1)[0];
                        editOrderPromoCode.value.splice(newIndex, 0, rowSelected);

                        for (let i = 0; i < editOrderPromoCode.value.length; i++) {
                            editOrderPromoCode.value[i].order = i + 1
                        }
                        editOrderPromoCode.value = quickSortPromoCode(editOrderPromoCode.value);
                    }
                }
            )
        });

        const tablePromoCode = computed(() => {
            return isEditOrderMode.value ? editOrderPromoCode.value : sortedPromoCode.value
        })

        return {
            promoCodeItems,
            isPromoCodePending,

            headers,
            tablePromoCode,

            editPromoCodeDialogShown,
            confirmCancelItemDialogShown,
            confirmSaveItemDialogShown,
            initAddPromoCode,
            initEditPromoCode,
            deletePromoCode,
            isEditPromoCodeFormValid,
            editingPromoCode,
            savePromoCode,
            cancelEditPromoCodeItem,

            DateTime,
            getFileUrl: root.$getFileURL,

            previewHtmlContentOption,
            currentPagination,

            initEditOrder,
            saveEditOrder,
            cancelEditOrder,

            isEditOrderMode,
            overlay,

            types,
        }
    }
}
</script>
